// Тематична візуалізація енергооб'єкта — ізометричні фотомодулі з м'яким
// сяйвом. Той самий малюнок, що вже вживає EMS Box; винесений сюди, бо там це
// локальна функція в чужому файлі.
//
// Це НЕ фотографія і не окрема картка: чистий SVG на токенах кабінету, тож у
// світлій і темній темі він однаковий за настроєм і нічого не важить у мережі.
// Яскравість сяйва веде `frac` — частка поточного завантаження, тож малюнок
// живе разом із даними, а не стоїть декорацією.
//
// Ідентифікатори градієнтів мають префікс: інакше два екземпляри на одній
// сторінці перебивали б <defs> один одного.
import { useId } from 'react';

export default function SolarArrayViz({ frac = 0, className, style }: {
  frac?: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  const uid = useId().replace(/:/g, '');
  const cellId = `cell-${uid}`;
  const glowId = `glow-${uid}`;

  const cols = 6, rows = 4, cw = 42, ch = 21;
  const glow = 0.14 + 0.5 * Math.max(0, Math.min(1, frac));

  const layer = (ox: number, oy: number, op: number, pfx: string) => {
    const proj = (i: number, j: number) => [ox + ((i - j) * cw) / 2, oy + ((i + j) * ch) / 2] as const;
    const out = [];
    const A = proj(0, 0), B = proj(cols, 0), C = proj(cols, rows), D = proj(0, rows);
    out.push(
      <polygon key={`${pfx}-f`} points={`${A} ${B} ${C} ${D}`} fill="var(--bg)"
        fillOpacity={op * 0.92} stroke="var(--line-strong)" strokeWidth={1} strokeOpacity={op} />,
    );
    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        const p0 = proj(i, j), p1 = proj(i + 1, j), p2 = proj(i + 1, j + 1), p3 = proj(i, j + 1);
        out.push(
          <polygon key={`${pfx}-${i}-${j}`} points={`${p0} ${p1} ${p2} ${p3}`}
            fill={`url(#${cellId})`} fillOpacity={op} stroke="var(--text)"
            strokeWidth={0.55} strokeOpacity={0.18 * op} />,
        );
      }
    }
    return out;
  };

  return (
    <svg viewBox="0 0 320 180" className={className} style={{ width: '100%', height: 'auto', ...style }} aria-hidden>
      <defs>
        <linearGradient id={cellId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--surface-solid)" />
          <stop offset="1" stopColor="var(--bg)" />
        </linearGradient>
        <radialGradient id={glowId} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="var(--text)" stopOpacity={glow} />
          <stop offset="1" stopColor="var(--text)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="160" cy="96" rx="158" ry="82" fill={`url(#${glowId})`} />
      {layer(196, 34, 0.5, 'back')}
      {layer(132, 62, 1, 'front')}
      <circle cx="300" cy="26" r="13" fill="var(--warn)" opacity={0.18 + 0.4 * glow} />
      <circle cx="300" cy="26" r="7" fill="var(--warn)" opacity={0.85} />
    </svg>
  );
}
